/* eslint-disable eqeqeq */
/**
 * Phase 6 Vendor-Port W1.5 — UV-C CMS-PATH RESOLVER (§33.6, §31).
 *
 * WHY THIS EXISTS (the W1 MAJOR findings it fixes):
 *   The dependent UVC selects (Product, Power Supply, Voltage, Lamp Series, …)
 *   do NOT take their option labels from the flat master catalog lists
 *   (`/master/.../uvc_lamp/` etc. — those rows carry `model:"SEL-33-HO-T5"`,
 *   `spn`, `description`, but NOT the display `series`/`voltage` name). The
 *   correct labels live ONLY in the UVC CMS doc
 *   (`GET /api/v1.0/cms/1/v1.0.0/ahu/uvc_params/` — legacy
 *   `fn['current.cms.uvc']`, model.accessory.uvc.cms urlRoot), under a nested
 *   dot-path per field. Wave-1 bound Product to the lamp catalog (showed
 *   "SEL-33-HO-T5" not "RLM Extreme") and Power Supply/Voltage/Lamp Series to
 *   labelKeys that don't exist on the master rows (blank text). This module
 *   ports the legacy resolution faithfully.
 *
 * LEGACY EQUIVALENT (uvc.selector.js):
 *   - The doc-path map = `PARAMETER_CMS_PATHS_MAPPING` (defined identically in
 *     both `mergeIdsWithCMS` and `dataForRating`, lines 700-718 / 777-795).
 *   - The lookup = `fn.tools.get_from_cms_data(path, id|ids|null, UVCData)`
 *     (fn_tools.js:454): walk the dot-path into the CMS doc to an array, then
 *       • id == null  → the whole array (the OPTION LIST)
 *       • id is Array → rows whose id ∈ ids (the dependency-narrowed list)
 *       • id scalar   → the single matching row (the SELECTED row → its label)
 *   - The option <text> = legacy render block (lines 539-578):
 *       mfg            → row.name           ("UV Resources")
 *       series rows    → row.series         ("RLM Extreme", "Xbox-PS", …)
 *       voltage/value  → row.name || row.value ("120/1/60", "High", "Standard")
 *       model rows     → row.model          ("INSTL-KIT", "RLM-DLH-EB", …)
 *
 * ★ W3 REUSE SEAM (the dependency engine will need the SAME pattern):
 *   `getFromCmsData(doc, path, id)` is a verbatim port of
 *   `fn.tools.get_from_cms_data` — the universal CMS-doc accessor every vendor
 *   selector (fan/ewheel/phx) calls. W3's DependenciesHandler resolves each
 *   parameter's narrowed option list by calling the SAME accessor with the
 *   parameterPossibleIDs id-array (the `id is Array` branch). Keep this function
 *   generic (doc + dot-path + id) and vendor-agnostic; the UVC-specific bits
 *   (the path map, the label rules) live in `UVC_CMS_PATHS` / `cmsOptionLabel`
 *   below, which W3 can parameterize per vendor. Do NOT inline this into the
 *   component — W2-W6 import it.
 */

import type { CmsNamedRow } from '../cmsDoc';
import type { OptionRow } from '../formKit';

/** The UVC CMS doc shape is loose (nested arrays under dot-paths). */
export type UvcCmsDoc = Record<string, unknown>;

/**
 * The UVC CMS doc URL (legacy model.accessory.uvc.cms urlRoot). Fetched via the
 * shared `getComponentCmsDoc` RTKQ query (URL-keyed, 24h cache).
 */
export const UVC_CMS_DOC_URL = '/api/v1.0/cms/1/v1.0.0/ahu/uvc_params/';

/**
 * field-name → CMS doc dot-path. Verbatim from legacy
 * PARAMETER_CMS_PATHS_MAPPING (the two copies are identical). Only the fields
 * whose LABELS live in the CMS doc are listed; the master-catalog selects
 * (lamp_output, lamp_finish via master list, etc.) keep their catalog binding.
 *
 * NB: `framing_kit` in the doc (single 'm') vs the legacy state key
 * `frameing_kit_id` (mis-spelled). The path string uses the DOC spelling.
 */
export const UVC_CMS_PATHS: Record<string, string> = {
  mfg_id: 'mfg',
  product_series_id: 'series',
  power_supply_series_id: 'power_supply.series',
  power_supply_voltage_id: 'power_supply.voltage',
  lamp_series_id: 'lamp.series',
  uv_repeat_kit_series_id: 'uv_repeat_kit.series',
  lamp_finish_id: 'lamp.finish',
  lamp_output_id: 'lamp.lamp_output',
  frameing_kit_id: 'framing_kit.model',
  lamp_holder_id: 'lamp_holder.model',
  balast_connector_kit_id: 'balast_connector_kit.model',
  radio_meter_id: 'radio_meter',
  hour_meter_id: 'hour_meter',
  current_sensor_id: 'current_sensor.model',
  intlk_contact_controller_id: 'intlk_contact_controller.model',
  inter_lock_switch_id: 'inter_lock_switch.model',
  warning_sign_id: 'warning_sign.model',
};

/**
 * ★ W3 REUSE SEAM — verbatim port of fn.tools.get_from_cms_data (fn_tools.js:454).
 * Walk a dot-path into the (already-fetched, plain-JSON) CMS doc, then:
 *   - id == null      → the whole array at that path (the option list)
 *   - Array.isArray   → rows whose id ∈ ids (dependency-narrowed list)
 *   - scalar id       → the single row whose id == id (the selected row)
 * Returns `undefined` when the path or id misses (legacy returns the JS
 * `find`/`filter`/array result; callers null-check).
 */
export function getFromCmsData(
  doc: UvcCmsDoc | undefined,
  path: string,
  id?: number | string | Array<number | string> | null
): CmsNamedRow | CmsNamedRow[] | undefined {
  if (!doc) return undefined;
  let node: unknown = doc;
  for (const seg of path.split('.')) {
    if (node == null) return undefined;
    node = (node as Record<string, unknown>)[seg];
  }
  if (!Array.isArray(node)) return undefined;
  const arr = node as CmsNamedRow[];
  if (id == null) return arr;
  if (Array.isArray(id)) {
    const ids = new Set(id.map((v) => String(v)));
    return arr.filter((e) => ids.has(String(e.id)));
  }
  return arr.find((e) => String(e.id) == String(id));
}

/**
 * Fields whose option text is the row's `model` (NOT `series`), matching the
 * legacy render block's first branch (uvc.selector.js:544):
 *   warning_sign | balast_connector_kit | frameing_kit | intlk_contact_controller
 *   | current_sensor | lamp_holder  →  item.model
 * These rows DO carry a `series` too (e.g. lamp_holder series "UVR-RLM-LH"
 * vs model "RLM-DLH-EB"), so the precedence MUST be field-aware — a naive
 * series-first label would mis-display them.
 */
const MODEL_LABEL_FIELDS = new Set<string>([
  'warning_sign_id',
  'balast_connector_kit_id',
  'frameing_kit_id',
  'intlk_contact_controller_id',
  'current_sensor_id',
  'lamp_holder_id',
  // radio_meter/hour_meter/inter_lock_switch rows are model-only in the doc;
  // their `.model` path resolves the same way and `series` is absent/None.
  'radio_meter_id',
  'hour_meter_id',
  'inter_lock_switch_id',
]);

/**
 * The option <text> rule for a CMS row, matching the legacy render block
 * (uvc.selector.js 539-578), field-aware:
 *   mfg_id                 → name             ("UV Resources")
 *   MODEL_LABEL_FIELDS     → model            ("RLM-DLH-EB", "CU2", "WS-12X8" …)
 *   series fields          → series           ("RLM Extreme", "Xbox-P" …)
 *   voltage/value fields   → name || value    ("120/1/60", "High", "Standard")
 * Falls through series → model → name → value when `field` is omitted (e.g. a
 * defensive selected-label read where the caller passed no field).
 */
export function cmsOptionLabel(row: CmsNamedRow | undefined, field?: string): string {
  if (!row) return '';
  if (field === 'mfg_id') return String(row.name ?? '');
  if (field && MODEL_LABEL_FIELDS.has(field)) {
    if (row.model != null && row.model !== '') return String(row.model);
  }
  if (row.series != null && row.series !== '') return String(row.series);
  if (row.name != null && row.name !== '') return String(row.name);
  if (row.value != null && row.value !== '') return String(row.value);
  if (row.model != null && row.model !== '') return String(row.model);
  return '';
}

/**
 * Resolve, for one dependent select, the {value,label} OPTION LIST from the CMS
 * doc (id-list undefined ⇒ FULL list — Wave-1 read-only seeds the full list, a
 * superset, so the saved value always renders; W3 will pass the narrowed
 * id-array). value = row.id; label = cmsOptionLabel(row).
 *
 * Returns OptionRow[] (so the existing <select> rendering reuses formKit's
 * machinery), each row carrying `id` + a synthetic `label` field plus the raw
 * CMS row spread (so callers can read `series`/`model`/`generic_mfg` etc.).
 */
export function cmsOptionList(
  doc: UvcCmsDoc | undefined,
  field: string,
  ids?: Array<number | string> | null
): OptionRow[] {
  const path = UVC_CMS_PATHS[field];
  if (!path) return [];
  const result = getFromCmsData(doc, path, ids ?? null);
  const arr = Array.isArray(result) ? result : result ? [result] : [];
  return arr.map((row) => ({
    ...row,
    id: row.id,
    label: cmsOptionLabel(row, field),
  })) as OptionRow[];
}

/**
 * Resolve the DISPLAY LABEL for the currently-selected id of a dependent select
 * (legacy: get_from_cms_data(path, selectedId, doc) → cmsOptionLabel). Used for
 * the selected-value text + as the read-back when an option list is empty.
 */
export function cmsSelectedLabel(
  doc: UvcCmsDoc | undefined,
  field: string,
  id: number | string | null | undefined
): string {
  if (id == null || id === '') return '';
  const path = UVC_CMS_PATHS[field];
  if (!path) return '';
  const row = getFromCmsData(doc, path, id);
  return cmsOptionLabel(Array.isArray(row) ? row[0] : row, field);
}

/** Convenience: is this field resolved via the CMS doc (vs a master catalog)? */
export function isCmsResolvedField(field: string): boolean {
  return field in UVC_CMS_PATHS;
}
